import { useCallback, useEffect, useMemo, useState } from 'react';
import ModuleFilters, { TgetFiltersList } from 'components/ModuleFilters/ModuleFilters';
import { listOrganizations } from 'services/bussines';
import useFiltersState from 'state/useFiltersState';
import { ISellerBillingEntity } from 'types/billing.types';
import { ICountryDetail, Iorganization } from 'types/bussines';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { monthFilterRawOptions } from 'utils/variablesToTranslate';
import './BillingModuleListingFilters.scss';

interface BillingModuleListingFiltersProps {
  listOfCountries: ICountryDetail[];
  listOfBillingEntityBord: ISellerBillingEntity[];
  availableMonths?: string[];
  monthValueMap?: Map<string, string>;
}

const BillingModuleListingFilters = ({
  listOfCountries,
  listOfBillingEntityBord,
  availableMonths = [],
  monthValueMap = new Map()
}: BillingModuleListingFiltersProps) => {
  const { billingModuleListingFilters, setBillingModuleListingFilters } = useFiltersState();
  const [organizationsList, setOrganizationsList] = useState<Iorganization[]>([]);
  const [openCountryFilter, setOpenCountryFilter] = useState<boolean>(false);
  const [openBillingEntityBordFilter, setOpenBillingEntityBordFilter] = useState<boolean>(false);
  const [openOrganizationFilter, setOpenOrganizationFilter] = useState<boolean>(false);
  const [openMonthFilter, setOpenMonthFilter] = useState<boolean>(false);
  const [openBillingStatusFilter, setOpenBillingStatusFilter] = useState<boolean>(false);
  const [openReceiptStatusFilter, setOpenReceiptStatusFilter] = useState<boolean>(false);

  const billingModuleListingFiltersMapping = {
    Country: 'countriesIds',
    BillingEntityBord: 'billingEntityBord',
    Client: 'organizationIds',
    Month: 'months',
    BillingStatus: 'billingStatus',
    ReceiptStatus: 'receiptStatus'
  };

  const billingStatusNames = ['Factura cargada', 'Por cargar factura'];
  const receiptStatusNames = ['Comprobante cargado', 'Por cargar comprobante'];

  const billingStatusNameToValueMap = new Map<string, string>([
    ['Factura cargada', 'charged'],
    ['Por cargar factura', 'pending']
  ]);

  const receiptStatusNameToValueMap = new Map<string, string>([
    ['Comprobante cargado', 'charged'],
    ['Por cargar comprobante', 'pending']
  ]);

  const billingEntityBordRawOptions = useMemo(
    () =>
      listOfBillingEntityBord
        ?.map(billingEntityBord => billingEntityBord?.name)
        ?.filter((name): name is string => Boolean(name))
        ?.sort((a, b) => a.localeCompare(b)) ?? [],
    [listOfBillingEntityBord]
  );

  const allCountriesRawOptions = useMemo(() => {
    if (!listOfCountries || listOfCountries?.length === 0) return [];
    return listOfCountries.reduce<string[]>((accum, current) => {
      if (current.name) accum.push(current.name);
      return accum;
    }, []);
  }, [listOfCountries]);

  const organizationsRawOptions = useMemo(
    () =>
      organizationsList
        ?.map(organization => organization?.name)
        ?.filter((name): name is string => Boolean(name))
        ?.sort((a, b) => a.localeCompare(b)) ?? [],
    [organizationsList]
  );

  const monthsToUse = useMemo(
    () => (availableMonths?.length > 0 ? availableMonths : monthFilterRawOptions || []),
    [availableMonths]
  );

  const monthToNumberMap = useMemo<Map<string, string>>(() => {
    if (availableMonths?.length > 0 && monthValueMap?.size > 0) {
      return monthValueMap;
    }
    if (monthFilterRawOptions) {
      return new Map(monthFilterRawOptions.map((month, i) => [month, String(i + 1)]));
    }
    return new Map();
  }, [availableMonths?.length, monthValueMap]);

  const organizationsNameToIdMap = useMemo(
    () => new Map(organizationsList.map(organization => [organization?.name, String(organization?.id)])),
    [organizationsList]
  );

  const countryNameToCodeMap = useMemo(
    () => new Map(listOfCountries.map(countryData => [countryData?.name, String(countryData?.id)])),
    [listOfCountries]
  );

  const getOrganizationsList = async () => {
    try {
      const organizations = await listOrganizations();
      setOrganizationsList(organizations);
    } catch (error) {
      displayErrorNotification();
    }
  };

  const getBillingModuleListingFiltersList: TgetFiltersList = useCallback(
    (formModuleFilters, handleClickFilterOption, getFilterActiveStatus) => [
      {
        title: 'Bodega o país',
        openFunction: setOpenCountryFilter,
        openStatus: openCountryFilter,
        rawSelectOptions: formModuleFilters('Country', allCountriesRawOptions, countryNameToCodeMap),
        handleChangeSelectedOption: handleClickFilterOption('countriesIds', 'value'),
        isFilterActive: getFilterActiveStatus('countriesIds'),
        sortAlphabetically: true,
        searchPlaceholder: 'Buscador',
        showSearch: true
      },
      {
        title: 'Razón social de Bord',
        openFunction: setOpenBillingEntityBordFilter,
        openStatus: openBillingEntityBordFilter,
        rawSelectOptions: formModuleFilters('BillingEntityBord', billingEntityBordRawOptions),
        handleChangeSelectedOption: handleClickFilterOption('billingEntityBord', 'name'),
        isFilterActive: getFilterActiveStatus('billingEntityBord'),
        searchPlaceholder: 'Buscador',
        showSearch: true
      },
      {
        title: 'Empresa',
        openFunction: setOpenOrganizationFilter,
        openStatus: openOrganizationFilter,
        rawSelectOptions: formModuleFilters('Client', organizationsRawOptions, organizationsNameToIdMap),
        handleChangeSelectedOption: handleClickFilterOption('organizationIds', 'value'),
        isFilterActive: getFilterActiveStatus('organizationIds'),
        searchPlaceholder: 'Buscador',
        showSearch: true
      },
      {
        title: 'Mes',
        openFunction: setOpenMonthFilter,
        openStatus: openMonthFilter,
        rawSelectOptions: formModuleFilters('Month', monthsToUse, monthToNumberMap),
        handleChangeSelectedOption: handleClickFilterOption('months', 'value'),
        isFilterActive: getFilterActiveStatus('months')
      },
      {
        title: 'Estado de la factura',
        openFunction: setOpenBillingStatusFilter,
        openStatus: openBillingStatusFilter,
        rawSelectOptions: formModuleFilters('BillingStatus', billingStatusNames, billingStatusNameToValueMap),
        handleChangeSelectedOption: handleClickFilterOption('billingStatus', 'value'),
        isFilterActive: getFilterActiveStatus('billingStatus')
      },
      {
        title: 'Estado del comprobante',
        openFunction: setOpenReceiptStatusFilter,
        openStatus: openReceiptStatusFilter,
        rawSelectOptions: formModuleFilters('ReceiptStatus', receiptStatusNames, receiptStatusNameToValueMap),
        handleChangeSelectedOption: handleClickFilterOption('receiptStatus', 'value'),
        isFilterActive: getFilterActiveStatus('receiptStatus')
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allCountriesRawOptions,
      billingEntityBordRawOptions,
      countryNameToCodeMap,
      monthsToUse,
      monthToNumberMap,
      openBillingEntityBordFilter,
      openBillingStatusFilter,
      openCountryFilter,
      openMonthFilter,
      openOrganizationFilter,
      openReceiptStatusFilter,
      organizationsNameToIdMap,
      organizationsRawOptions
    ]
  ) as TgetFiltersList;

  useEffect(() => {
    getOrganizationsList();
  }, []);

  return (
    <div className="billingModuleListingFilters">
      <ModuleFilters
        moduleFilters={billingModuleListingFilters}
        setModuleFilters={setBillingModuleListingFilters}
        getFiltersList={getBillingModuleListingFiltersList}
        countryCondition
        moduleFiltersTitlesMapping={billingModuleListingFiltersMapping}
        customGap={24}
        singleSelectFilters={{ billingStatus: true, receiptStatus: true }}
      />
    </div>
  );
};

export default BillingModuleListingFilters;
